import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { EditIcon } from '@/icons';
import { commaSeparator } from '@/lib/commaSeparator';
import { Withdrawal } from '@/services/withdrawals/withdrawals.types';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { ManageWithdrawal } from '../../ManageWithdrawal';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

export function getColumns(onOpen: OnOpenProps, t: (key: string) => string): ColumnDef<Withdrawal>[] {
  return [
    {
      accessorKey: 'withdrawalId',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="truncate max-w-[1rem] text-caption font-medium text-primary">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'minAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.withdrawalSetup.affiliate.table.minimumAmount')}
        />
      ),
      cell: ({ row }) => {
        const value = Number(row.getValue('minAmount'));
        return (
          <div className="text-right text-caption font-medium text-primary">{commaSeparator(value.toFixed(0))} 円</div>
        );
      },
      meta: { label: 'Min Amount', align: 'right' },
      enableHiding: false,
    },
    {
      accessorKey: 'maxAmount',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.withdrawalSetup.affiliate.table.maximumAmount')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-right text-caption font-medium text-primary">
          {commaSeparator(row.getValue('maxAmount'))}円
        </div>
      ),
      meta: { label: 'Max Amount', align: 'right' },
      enableHiding: false,
    },
    {
      accessorKey: 'requestingDayFrom',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.withdrawalSetup.affiliate.table.startDate')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption font-medium text-primary">{row.getValue('requestingDayFrom')}</div>
      ),
      meta: { label: 'Requesting Day (From)' },
      enableHiding: false,
    },
    {
      accessorKey: 'requestingDayTo',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.withdrawalSetup.affiliate.table.endDate')}
        />
      ),
      cell: ({ row }) => <div className="text-caption font-medium text-primary">{row.getValue('requestingDayTo')}</div>,
      meta: { label: 'Requesting Day (To)' },
      enableHiding: false,
    },
    {
      accessorKey: 'withdrawableDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('configurationManagement.withdrawalSetup.affiliate.table.withdrawalDate')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-caption font-medium text-primary">{row.getValue('withdrawableDate')}</div>
      ),
      meta: { label: 'Withdraw-able Date' },
      enableHiding: false,
    },

    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      cell: ({ row }) => (
        <div className="flex gap-4">
          <Can rule={{ allOf: [PERMS.withdrawal.setup.detailView, PERMS.withdrawal.setup.manage] }}>
            <EditIcon
              className="text-foreground-subtle size-4 hover:cursor-pointer"
              onClick={() =>
                onOpen({
                  title: 'Manage Withdrawal',
                  okText: t('common.page.button.submit'),
                  size: 'lg',
                  modalTitle: 'configurationManagement.withdrawalSetup.manageWithdrawalModal.title',
                  component: <ManageWithdrawal withdrawalId={row.getValue('withdrawalId')} />,
                })
              }
            />
          </Can>
        </div>
      ),
      size: 40,
      enableHiding: false,
    },
  ];
}
